import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ConfiguracaoEmpresaDashboardDTO } from '../../../models/dashboard/configuracao-empresa-dashboard.model';

@Component({
  selector: 'app-dashboard-plano',
  templateUrl: './dashboard-plano.component.html',
  styleUrls: ['./dashboard-plano.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardPlanoComponent implements OnInit, OnDestroy {

  @Input() configuracaoEmpresa: ConfiguracaoEmpresaDashboardDTO | undefined;
  @Input() totalVendasNoMes = 0;

  texts = {
    planos: '',
    plano_atual: '',
    vencimento: '',
    dias_restantes: '',
    plano_vencido: '',
    data_de_vencimento: '',
    uso_do_plano: '',
    fechamento_ciclo: '',
    sem_plano_ativo: '',
    contratar_plano: '',
    sem_vencimento: '',
    sem_limite_vendas: ''
  };

  private langChangeSub!: Subscription;

  constructor(
    private translate: TranslateService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations(): void {
    this.texts = {
      planos: this.translate.instant('dashboard.texts.planos'),
      plano_atual: this.translate.instant('dashboard.texts.plano_atual'),
      vencimento: this.translate.instant('dashboard.texts.vencimento'),
      dias_restantes: this.translate.instant('dashboard.texts.dias_restantes'),
      plano_vencido: this.translate.instant('dashboard.texts.plano_vencido'),
      data_de_vencimento: this.translate.instant('dashboard.texts.data_de_vencimento'),
      uso_do_plano: this.translate.instant('dashboard.texts.uso_do_plano'),
      fechamento_ciclo: this.translate.instant('dashboard.texts.fechamento_ciclo'),
      sem_plano_ativo: this.translate.instant('dashboard.texts.sem_plano_ativo'),
      contratar_plano: this.translate.instant('dashboard.texts.contratar_plano'),
      sem_vencimento: this.translate.instant('dashboard.texts.sem_vencimento'),
      sem_limite_vendas: this.translate.instant('dashboard.texts.sem_limite_vendas')
    };
  }

  get temPlano(): boolean {
    return !!this.configuracaoEmpresa?.inscricaoAtual?.plano;
  }

  /**
   * Plano ESPECIAL (ex.: "Plan Contador"): perpétuo, sem vencimento e sem limite
   * de vendas. Nesse caso o card exibe rótulos "sem vencimento"/"sem limite" em
   * vez de datas vazias e gauge/dias zerados.
   */
  get ehPlanoEspecial(): boolean {
    return this.configuracaoEmpresa?.inscricaoAtual?.plano?.tipoPlano === 'ESPECIAL';
  }

  get planoNome(): string {
    return this.configuracaoEmpresa?.inscricaoAtual?.plano?.nome || '';
  }

  get planoDescricao(): string {
    return this.configuracaoEmpresa?.inscricaoAtual?.plano?.descricao || '';
  }

  get ehPlanoStart(): boolean {
    return this.configuracaoEmpresa?.inscricaoAtual?.plano?.planoStart || false;
  }

  get percentualUsoPlano(): number {
    // `limiteDeGastoEfetivo` ja considera o limite NEGOCIADO por cupom especial; o plano
    // sozinho traria zero para quem contratou por cupom.
    const limite = this.configuracaoEmpresa?.inscricaoAtual?.limiteDeGastoEfetivo
      ?? this.configuracaoEmpresa?.inscricaoAtual?.plano?.limiteDeGasto
      ?? 1;
    const limiteReal = limite === 0 ? 1 : limite;
    const percentual = (this.totalVendasNoMes / limiteReal) * 100;
    return Math.min(Math.round(percentual), 100);
  }

  get percentualVencimento(): number {
    const inscricao = this.configuracaoEmpresa?.inscricaoAtual;
    if (!inscricao?.dataDeInscricao || !inscricao?.dataDeCancelamento) return 0;
    const total = new Date(inscricao.dataDeCancelamento).getTime()
                  - new Date(inscricao.dataDeInscricao).getTime();
    const passado = Date.now() - new Date(inscricao.dataDeInscricao).getTime();
    return Math.min(Math.max(Math.round((passado / total) * 100), 0), 100);
  }

  // Interpreta uma data "YYYY-MM-DD" como data LOCAL. Evita o recuo de 1 dia
  // causado por new Date(string), que interpreta a string como UTC e, em fusos
  // negativos (ex.: Paraguai UTC-4), faz getDate() retornar o dia anterior.
  private parseDataLocal(data: string): Date {
    const [ano, mes, dia] = data.substring(0, 10).split('-').map(Number);
    return new Date(ano, mes - 1, dia);
  }

  get diasRestantes(): number {
    const dataVencimento = this.configuracaoEmpresa?.inscricaoAtual?.dataDeCancelamento;
    if (!dataVencimento) return 0;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const diff = this.parseDataLocal(dataVencimento).getTime() - hoje.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  }

  get diasRestantesLabel(): string {
    const dias = this.diasRestantes;
    return dias < 0 ? this.texts.plano_vencido : `${dias}`;
  }

  get dataVencimentoFormatada(): string {
    const data = this.configuracaoEmpresa?.inscricaoAtual?.dataDeCancelamento;
    if (!data) return '';
    // Formata direto da string "YYYY-MM-DD" para evitar conversao de fuso.
    const [ano, mes, dia] = data.substring(0, 10).split('-');
    return `${dia}/${mes}/${ano}`;
  }

  // Proxima data de fechamento do ciclo de 30 dias (reinicio do limite de
  // vendas), contada a partir da data de ativacao: ativacao + 30, +60, +90...
  // Retorna a proxima a partir de hoje. Mesma regra usada na tela de planos.
  get dataFechamentoCicloFormatada(): string {
    const dataInscricao = this.configuracaoEmpresa?.inscricaoAtual?.dataDeInscricao;
    if (!dataInscricao) return '';
    const ativacao = this.parseDataLocal(dataInscricao);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    let k = 1;
    let proxima = new Date(ativacao.getFullYear(), ativacao.getMonth(), ativacao.getDate() + 30 * k);
    while (proxima.getTime() < hoje.getTime()) {
      k++;
      proxima = new Date(ativacao.getFullYear(), ativacao.getMonth(), ativacao.getDate() + 30 * k);
    }
    const dia = String(proxima.getDate()).padStart(2, '0');
    const mes = String(proxima.getMonth() + 1).padStart(2, '0');
    return `${dia}/${mes}/${proxima.getFullYear()}`;
  }

  abrirWhatsAppPlanos(): void {
    window.open('https://wa.me/595994360296', '_blank');
  }

  navegarParaPlanos(): void {
    this.router.navigate(['/planos']);
  }
}
